import comecando from "../docs/comecando.md?raw";
import primeiraAverbacao from "../docs/primeira-averbacao.md?raw";
import integracaoPrefeitura from "../docs/integracao-prefeitura.md?raw";
import webhooks from "../docs/webhooks.md?raw";
import erros from "../docs/erros.md?raw";
import glossario from "../docs/glossario.md?raw";
import changelog from "../docs/changelog.md?raw";

/**
 * Conteudo narrativo da documentacao de integracao, embutido no bundle.
 *
 * Antes vivia num site Docusaurus publico (`apps/docs`). Foi trazido pra dentro
 * do produto porque a documentacao passou a ser por convenio: teto de parcelas,
 * teto de juros e exigencia de CCB sao posicao comercial entre averbadora,
 * prefeitura e banco — num site aberto, um banco leria as condicoes do outro no
 * mesmo convenio.
 *
 * Os arquivos sao markdown de verdade (`src/docs/*.md`), importados com `?raw` e
 * renderizados com GFM. Ficam em markdown, e nao em JSX, por dois motivos: eram
 * markdown no Docusaurus e migraram sem reescrita, e conteudo longo em JSX vira
 * ilegivel pra quem so quer corrigir um paragrafo.
 */
export interface DocPage {
  slug: string;
  titulo: string;
  /** Uma linha na sidebar, abaixo do titulo. */
  resumo: string;
  markdown: string;
}

export interface DocGroup {
  titulo: string;
  paginas: DocPage[];
}

/** Remove o frontmatter YAML herdado do Docusaurus. */
function semFrontmatter(md: string): string {
  return md.startsWith("---") ? md.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, "") : md;
}

const pagina = (slug: string, titulo: string, resumo: string, markdown: string): DocPage => ({
  slug,
  titulo,
  resumo,
  markdown: semFrontmatter(markdown),
});

export const DOC_GROUPS: DocGroup[] = [
  {
    titulo: "Começando",
    paginas: [
      pagina("comecando", "Visão geral", "Camadas, autenticação e escopos", comecando),
      pagina("primeira-averbacao", "Primeira averbação", "Do token ao contrato aprovado", primeiraAverbacao),
      pagina("integracao-prefeitura", "Integração — Prefeitura", "Carga inicial, remessa, retorno e ADF", integracaoPrefeitura),
    ],
  },
  {
    titulo: "Integração",
    paginas: [
      pagina("webhooks", "Webhooks", "Os 18 eventos, assinatura e retentativa", webhooks),
      pagina("erros", "Erros", "Envelope, status e códigos de negócio", erros),
    ],
  },
  {
    titulo: "Apoio",
    paginas: [
      pagina("glossario", "Glossário", "ADF, convênio, margem, competência", glossario),
      pagina("changelog", "Changelog", "Mudanças de contrato, da mais recente", changelog),
    ],
  },
];

export const DOC_PAGES: DocPage[] = DOC_GROUPS.flatMap((g) => g.paginas);

export function acharPagina(slug: string | undefined): DocPage | undefined {
  return slug ? DOC_PAGES.find((p) => p.slug === slug) : undefined;
}

/** Títulos de nível 2 do markdown, para o índice lateral da página aberta. */
export function ancoras(md: string): { id: string; texto: string }[] {
  return [...md.matchAll(/^##\s+(.+)$/gm)].map((m) => {
    const texto = m[1]!.replace(/[#*`]/g, "").trim();
    return { id: slugificar(texto), texto };
  });
}

/** Mesmo slug que o renderer aplica nos headings, pra âncora e índice casarem. */
export function slugificar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

/** Um acerto da busca: a página, o trecho ao redor da 1ª ocorrência (partido no
 *  termo, pra o preview poder marcá-lo) e quantas vezes o termo aparece na página. */
export interface ResultadoBusca {
  pagina: DocPage;
  /** Texto antes do termo, no trecho (com reticências à esquerda se cortado). */
  antes: string;
  /** O termo como aparece no texto (casing original). */
  casado: string;
  /** Texto depois do termo, no trecho. */
  depois: string;
  /** Ocorrências na página inteira — orienta quem lê a decidir onde clicar. */
  ocorrencias: number;
}

/** Limpa ruído de markdown do trecho de preview (não do termo casado). */
function limparTrecho(s: string): string {
  return s.replace(/\s+/g, " ").replace(/[#*`|>]/g, "").trim();
}

/**
 * Busca sobre o texto das páginas. Devolve, por página que casa, o trecho ao
 * redor da primeira ocorrência já partido em `antes`/`casado`/`depois` — o preview
 * marca o `casado` — e o total de ocorrências na página, que a sidebar mostra.
 */
export function buscarNoConteudo(termo: string): ResultadoBusca[] {
  const q = termo.trim().toLowerCase();
  if (q.length < 3) return [];
  const out: ResultadoBusca[] = [];
  for (const p of DOC_PAGES) {
    const baixo = p.markdown.toLowerCase();
    const i = baixo.indexOf(q);
    if (i === -1) continue;
    let ocorrencias = 0;
    for (let j = i; j !== -1; j = baixo.indexOf(q, j + q.length)) ocorrencias++;
    const ini = Math.max(0, i - 60);
    out.push({
      pagina: p,
      antes: `${ini > 0 ? "… " : ""}${limparTrecho(p.markdown.slice(ini, i))}`,
      casado: p.markdown.slice(i, i + q.length),
      depois: `${limparTrecho(p.markdown.slice(i + q.length, i + q.length + 90))} …`,
      ocorrencias,
    });
  }
  return out;
}
